import React, {useEffect, useRef, useState} from 'react';
import Panel from './layout/Panel';
import SearchField from './layout/SearchField';
import Spacer from './layout/Spacer';
import ListView from './listComponent/ListView';
import Button from './layout/Button';
import ListItem from './listComponent/ListItem';
import ObjectPreview from './ObjectPreview';
import useForceUpdate from './reactUtils/useForceUpdate';
import Engine from '../classes/Engine';
import ProjectSaver from '../classes/ProjectSaver';
import {LayoutOrientations, SaveModificationTypes} from '../globals'
import SceneObjectUtils from '../classes/utilities/SceneObjectUtils';
import TextUtils from '../classes/utilities/TextUtils';
import { assert } from '../classes/utilities/ErrorHandling';
import { getUuidv4 } from '../classes/utilities/Misc';

interface ISceneObjectPickerProps
{
	/** the guid of the currently selected scene object */
	value: string;
	/** a callback function that will be executed when any of the entries in the list is selected by the user, with the guid of the selected object as the parameter */
	onSelectedCallback?: (selectedObjectId: any) => void;
	/** a callback function that will be executed when any of the entries in the list is selected by the user, passing no parameters. this will mostly be used when showing the picker through a modal dialog */
	onShutdown?: () => void;
	sceneId?: string;
	multiSelectEnabled?: boolean;
	renderPreview?: boolean;
	returnObjectGuid?: boolean;
}

/**
 * shows a list of objects from the currently loaded scene so that the user can choose one of them  
 * *intended for use with a SceneObjectProperty*
 * @export
 * @param {ISceneObjectPickerProps} props
 * @return {*} a list of selectable scene objects, with buttons to cancel or confirm the selection
 */
export default function SceneObjectPicker(props: ISceneObjectPickerProps)
{
	const forceUpdate = useForceUpdate();

	const [selectedObjectId, setSelectedObjectId] = useState(props.value);
	const [selectedObjects, setSelectedObjects] = useState<Array<any>>([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [guidSet, setGuidSet] = useState(new Set());

	const selfRef = useRef(null);
	const guid = useRef(getUuidv4());
	const previouslyFocused = useRef(document.activeElement);
	const returnObjectGuid = props.returnObjectGuid ?? true;	
	console.log("[SceneObjectPicker] sceneId...", props.sceneId);
	const root = Engine.getInstance().getSelectionRoot(props.sceneId);
	console.log("[SceneObjectPicker] root", root);

	useEffect(() =>
	{
		if (selfRef.current)
		{
			// document.getElementById(guid.current).focus();
			//For some reason it doesn't focus when done here, so delay it to the next event loop cycle...
			const o = document.getElementById(guid.current);
			setTimeout(() =>{o?.focus();}, 0);
		}

		
		if(selectedObjectId)
		{
			const guidSet = new Set();
			guidSet.add(selectedObjectId);
			setGuidSet(guidSet);
		}
		//TODO: initialise guidSet when multiple objects are selected (via ModelTrackerModelPicker)
	
		return ()=>
		{
			if (previouslyFocused.current)
			{
				(previouslyFocused.current as HTMLElement)?.focus();
			}
		}
	}, [selfRef.current]);

	function renderChildrenOfObject(object)
	{
		if (!object)
			return [];

		const renderObjects = new Array<JSX.Element>();
		for (let index = 0; index < object.children.length; index++)
		{
			const child = object.children[index];

			// filter invisible in hierarchy
			if (child.userData.hideInHierarchy)
			{
				continue;
			}

			const guid = child.userData.guid ? child.userData.guid : getUuidv4();
			const item =
				<ListItem
				  key={guid}
				  nodeId={guid}
				  objectRef={child}
				  label={(child.name ? child.name : child.type)}
				  parent={object === root ? null : object}
				>
				  {
				    renderChildrenOfObject(child)
				  }
				</ListItem>

			renderObjects.push(item);
		}
		return renderObjects;
	}

	function renderHierarchy()
	{
		if (!root)
			return null;

		return renderChildrenOfObject(root);
	}

	function shouldFilterObject(object)
	{
		// root shouldn't appear
		if (object === root)
			return true;

		// objects hidden shouldn't appear
		if (object.userData.hideInHierarchy)
			return true;

		// objects that don't include the search term shouldn't appear
		const objectLabel = (object.name ? object.name : object.type);
		if (!TextUtils.searchString(objectLabel, searchQuery))
			return true;

		// eveything else should appear
		return false;
	}

	function renderFilteredHierarchy()
	{
		if (!root)
			return null;

		const output = new Array<JSX.Element>();
		console.log("[SceneObjectPicker] renderFilteredHierarchy root", root);
		root.traverse(child =>
		{
			if (!shouldFilterObject(child))
			{
				const guid = child.userData.guid ?? getUuidv4();
				output.push(
					<ListItem
						key={guid}
						nodeId={guid}
						objectRef={child}
						label={(child.name ?? child.type)}
						parent={null}
					/>
				);
			}
		});

		return output;
	}

	function onObjectSelected(selectedItems, deselectedItems)
	{
		console.log('[SceneObjectPicker] Object selected:', {
			selectedItems: Array.from(selectedItems).map((item: any) => ({
				nodeId: item.nodeId,
				label: item.label,
				objectRef: item.objectRef
			})),
			deselectedItems: Array.from(deselectedItems).map((item: any) => ({
				nodeId: item.nodeId,
				label: item.label,
				objectRef: item.objectRef
			})),
			returnObjectGuid,
			multiSelectEnabled: props.multiSelectEnabled
		});

		if(returnObjectGuid)
		{
			assert(!props.multiSelectEnabled); //no multi-object support for guid mode, yet
			const value = selectedItems.values().next().value;
			if(value)
			{
				setSelectedObjectId(value.nodeId);
				setGuidSet(new Set().add(value.nodeId));
			}
			else
			{
				setSelectedObjectId("");
			}
			return;
		}

		if(selectedItems.size)
		{
			const guidSet = new Set<string | null>();
			const selectedObjects = new Array<any>();
			for(const item of selectedItems)
			{
				if(item.nodeId)
				{
					selectedObjects.push(item.objectRef);
				}
				else
				{
					guidSet.add(null);
				}
			}

			if(deselectedItems.size)
			{
				const deselectedObjects = new Array<any>();
				for(const item of deselectedItems)
				{
					if(item.nodeId)
					{
						deselectedObjects.push(item.objectRef);
					}
					else
					{
						guidSet.delete(null);
					}
				}

				for(const deslectedObject of deselectedObjects)
				{
					if(selectedObjects.includes(deslectedObject))
					{
						selectedObjects.splice(selectedObjects.indexOf(deslectedObject), 1);
					}
				}
			}
						
			for(const object of selectedObjects)
			{
				guidSet.add(object.userData.guid);
			}
			setSelectedObjects(selectedObjects);
			setGuidSet(guidSet);
		}
		else
		{
			setSelectedObjects([]);
		}

		forceUpdate();
	}

	function onObjectPicked()
	{
		console.log('[SceneObjectPicker] Object picked:', {
			selectedObjectId,
			selectedObjects,
			returnObjectGuid,
			multiSelectEnabled: props.multiSelectEnabled
		});

		if (props.onSelectedCallback)
		{
			if(returnObjectGuid)
			{
				props.onSelectedCallback(selectedObjectId);
				if(selectedObjectId) ProjectSaver.getInstance().markModifiedObject(SceneObjectUtils.findObjectByGuid(root, selectedObjectId)!, SaveModificationTypes.Reference);
			}
			else
			{
				props.onSelectedCallback(selectedObjects);
			}
		}

		if (props.onShutdown)
		{
			props.onShutdown();
		}
	}

	return (
		<Panel
			data-name={props["data-name"] ? props["data-name"] : "scene-object-picker-root"}
			orientation={LayoutOrientations.Horizontal}>
			<div>
				<Panel className="solid composite"
					ref={selfRef}		
					style={{
						maxHeight: "500px",
						maxWidth: "500px",
					}}
				>
					<SearchField 
						onChangeCallback={(v: string) => setSearchQuery(v)} 
						id={guid.current}
						value={searchQuery}
					/>
					<Spacer size={{ y: "2px" }} />
					<ListView
						selectedItems={guidSet}
						selectionChangedCallback={onObjectSelected}
						multiSelectEnabled={props.multiSelectEnabled}
						doubleClickCallback={onObjectPicked}
						dndDefaults={{
							dragEnabled: false,
							dropEnabled: false,
						}}
					>
						<ListItem
							key={"none"}
							nodeId={null}
							label={" - None - "}
							parent={null}
						/>
						{
							!root ? null :
								searchQuery === "" ? renderHierarchy() :
									renderFilteredHierarchy()
						}
					</ListView>
					<Button
						category={"Scene Hierarchy"}
						label="Confirm"
						onClick={onObjectPicked}
					/>
				</Panel>
			</div>
			{props.renderPreview ?	
				<ObjectPreview
					objects={selectedObjects}
					parentGuid={guid.current}
				/> 
				: []}
		</Panel>	
	);
}